/**
 * THE MISSING LAYER — a tool page's option values become the ops it runs.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ This did not exist. Not "was broken" — was never built.                  │
 * │                                                                          │
 * │ `opsToApply` in `web/…/pages/tool.ts` spread the whole flat value bag    │
 * │ onto ops[0] and sent every later op bare:                                │
 * │                                                                          │
 * │   {"op":"normalize","normalize.respectLayers":true,                      │
 * │    "stripFormatting.enabled":false, …}                                   │
 * │   {"op":"stripFormatting"}                                               │
 * │                                                                          │
 * │ `NormalizeOp` has no field called `normalize.respectLayers`, so it ran   │
 * │ at defaults. `stripFormatting` received NOTHING — no scope, no keep-*    │
 * │ flags, no `where`. And `stripFormatting.enabled: false` — the user's     │
 * │ "leave my formatting alone" — sat on the NORMALIZE op, so the strip ran  │
 * │ anyway.                                                                  │
 * │                                                                          │
 * │ Every control on ten of the fifteen tool pages was inert, and the        │
 * │ download still produced a plausible file. A wrong file that looks right  │
 * │ is the worst thing this product can make.                                │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ── WHY IT LIVES HERE ───────────────────────────────────────────────────────
 *
 * The schema is a CONTROL SURFACE and an `Op` is a MACHINE SHAPE, and they are
 * deliberately not the same vocabulary. `shift` shows it plainly: the page asks
 * for a delta, a "use ranges" switch and a list of rows, while `ShiftOp` takes
 * `ranges` and `clampAtZero` and nothing else. No amount of key-mangling bridges
 * that — it needs a function, and only the side that owns the op knows which
 * one.
 *
 * So this sits in `src/` beside the engines rather than in `web/`. The page
 * imports it, which the boundary already permits — `web/…/lib/cue-window.ts`
 * imports `src/core/time/ticks.ts` today — and the shell stays tool-agnostic:
 * it hands over `(ops, values)` and gets `Op[]` back without knowing what any
 * tool does.
 *
 * ── THE KEY CONVENTION, AND WHY DOTTED WINS ─────────────────────────────────
 *
 * Two shipped side by side and nothing exercised the join:
 *
 *   `<op>.<field>`   CLEAN, TIMING     10 rows
 *   `<field>`        STRUCTURE          5 rows — and they work only because
 *                                       every one is a SINGLE-op row, so
 *                                       spreading onto ops[0] lands correctly
 *
 * Dotted is the ruled convention because a bare key cannot address a pipeline:
 * ten of the fifteen rows with options run more than one op, and `mode` means
 * one thing to `pinyin` and another to `mergeDocuments`. Bare keys are still
 * accepted and routed to ops[0] — that is what STRUCTURE's rows rely on, and
 * breaking five working pages to enforce a convention would be the cure killing
 * the patient.
 *
 * ── `<op>.enabled` IS A PIPELINE FILTER, NEVER AN OP FIELD ──────────────────
 *
 * Eight toggles across the cleaning pages, and no `Op` in the contract has an
 * `enabled` field. They decide whether the op RUNS. Passing one through would
 * be an unknown field on the op; dropping it silently is how "don't strip my
 * formatting" got ignored. It removes the op from the pipeline, which is what
 * the checkbox says it does.
 *
 * ── UNKNOWN OPS FAIL LOUDLY ────────────────────────────────────────────────
 *
 * An op with no builder throws. It does not fall back to `{op, ...values}` and
 * it does not silently drop: this whole class of defect is code that produced a
 * plausible result from a wrong input, and a builder table with a permissive
 * default would be the same mistake with a new name.
 */

import type {
  Alignment,
  Color,
  CueSelector,
  DocumentId,
  Framerate,
  FramerateSource,
  LineBreakOptions,
  Margins,
  MergeMode,
  Op,
  RawTagPolicy,
  SerialRegex,
  ShiftRange,
  TickDelta,
  Ticks,
} from '../../contracts/cue-model.ts';
import { FPS } from '../../contracts/cue-model.ts';
import { msToTicks, T } from '../core/time/ticks.ts';
import {
  CJK_DEFAULTS,
  CLAUSE_BREAKERS,
  LINE_BREAK_DEFAULTS,
  MERGE_SAME_TEXT_THRESHOLD,
} from '../transforms/clean/defaults.ts';
import {
  MUSIC_PATTERNS,
  SOUND_EFFECT_PATTERNS,
  WATERMARK_PATTERNS,
} from '../transforms/clean/patterns.ts';
import type { PatternEntry } from '../transforms/clean/patterns.ts';
import { hasQcControls, QC_NAMESPACE } from './qc-from-values.ts';

/** A tool page's flat value bag, exactly as the option panel produces it. */
export type ToolValues = Readonly<Record<string, unknown>>;

/**
 * EVERY OP GETS ONE, and no schema emits one.
 *
 * Thirteen ops carry `where: CueSelector` and not a single tool page exposes a
 * cue selector — the surface is "clean this file", not "clean cues 4 to 9". So
 * the whole document is the honest default, and it is stated here once rather
 * than repeated in fourteen builders where one could be forgotten.
 */
const ALL: CueSelector = { kind: 'all' };

/** The contract's stated UI default: leave a cue with animation tags alone and report it. */
const RAW_TAGS: RawTagPolicy = 'skip-cue';

// Readers take the bag and the key rather than the value, so every access is
// bracket notation in one place. `ToolValues` is an index signature by
// construction — it is whatever the option panel collected — and dotting into
// one is the kind of assumption this module exists to stop making.
const bool = (v: ToolValues, k: string, fallback: boolean): boolean =>
  typeof v[k] === 'boolean' ? (v[k] as boolean) : fallback;
const str = <T extends string>(v: ToolValues, k: string, fallback: T): T =>
  typeof v[k] === 'string' ? (v[k] as T) : fallback;
const num = (v: ToolValues, k: string, fallback: number): number =>
  typeof v[k] === 'number' ? (v[k] as number) : fallback;
/** `null` is a REAL value on this surface — an unset threshold means "off", not "use the default". */
const numOrNull = (v: ToolValues, k: string): number | null =>
  typeof v[k] === 'number' ? (v[k] as number) : null;

/*
 * ── MILLISECONDS IN, TICKS OUT ─────────────────────────────────────────────
 *
 * The page speaks milliseconds: a `duration` field is `parseDuration` → ms, and
 * every `number` field with `unit: 'ms'` is a millisecond count the user typed.
 * An op speaks ticks, 120 to the millisecond (`TICKS_PER_MS`). Nothing between
 * the two converts, so this layer does it, at the one place the field name and
 * the op field meet. Passing the raw number through moved a "2500" shift by
 * 2 500 ticks, which is 20.8 ms.
 */
const msDelta = (ms: number): TickDelta => T.delta(msToTicks(ms) as number);
/** An emptied `unit: 'ms'` box stays `null`, which is the op's own "off". */
const msDeltaOrNull = (ms: number | null): TickDelta | null => (ms === null ? null : msDelta(ms));

/**
 * The two ends of "the whole file" for a `ShiftRange`, whose bounds are
 * half-open (`from <= cue.start < to`).
 *
 * Safe integers rather than ±Infinity because an op is plain JSON that crosses
 * `postMessage` and can be logged or replayed as JSON, and `JSON.stringify`
 * turns Infinity into `null`. `to` is the same sentinel the shift engine's own
 * tests and golden fixture use for "unbounded". `from` is open as well, so the
 * global shift really is every cue, including one a parser left before zero.
 */
const START_OF_FILE = T.ticks(Number.MIN_SAFE_INTEGER);
const END_OF_FILE = T.ticks(Number.MAX_SAFE_INTEGER);

/**
 * A framerate box's value as a whole `Framerate`.
 *
 * The panel's `parseFramerate` yields `{num, den}` and a `Framerate` also has a
 * `label`, the token MicroDVD writes into its `{1}{1}<label>` header. The
 * framerate op makes `to` the document's rate, so a converted .sub file was
 * written with the header `{1}{1}undefined`, which no reader (ours included)
 * recognises as a rate. The label is the launch set's own when the rational is
 * one of them exactly (24000/1001 is '23.976'), else the rate in decimal.
 * Anything that is not a `{num, den}` passes through untouched, for the engine
 * to accept or refuse.
 *
 * Exported for the tool page's MicroDVD FPS prompt, whose parse takes a whole
 * `Framerate` too: one labelling rule, not one per caller.
 */
export function labelled(value: unknown): Framerate | null {
  if (typeof value !== 'object' || value === null) return null;
  const rate = value as Partial<Framerate>;
  if (typeof rate.label === 'string') return rate as Framerate;
  const { num, den } = rate;
  if (typeof num !== 'number' || typeof den !== 'number' || den === 0) return rate as Framerate;
  const known = Object.values(FPS).find((f) => f.num * den === num * f.den);
  const label = known?.label ?? String(Number((num / den).toFixed(3)));
  return { num, den, label };
}

/** The `SerialRegex` list for the pattern ids whose checkbox is on. */
function enabledPatterns(
  entries: readonly PatternEntry[],
  vals: ToolValues,
  prefix: string,
): readonly SerialRegex[] {
  return entries.filter((e) => bool(vals, `${prefix}.${e.id}`, e.defaultOn)).map((e) => e.re);
}

/**
 * What a run needs that no option control holds, because it comes from the
 * FILES rather than from the form. Only the two document-level ops read it.
 *
 *   others    the documents merged into the first, in the order the user
 *             added them. `mergeDocuments` takes the other document by id
 *             (`otherId`), and an id is something only the worker can mint,
 *             after a parse.
 *   cueStart  where the subtitle the user typed a number for starts, looked
 *             up in the file being split. `null` when the file has fewer
 *             subtitles than that. Only read when "cut before subtitle
 *             number" is filled in, and the page fetches it then.
 *
 * Absent means "the page has not supplied it", which is not the same as empty:
 * a merge with no second document is refused here rather than built as a merge
 * of one file, which would hand back the first file renumbered under a working
 * Download button.
 */
export interface RunInputs {
  readonly others?: readonly DocumentId[];
  readonly cueStart?: Ticks | null;
}

/**
 * What a row's page has to do beyond one file in and one file out, read off
 * its ops so the shell stays tool-agnostic: it asks this, not the slug.
 */
export interface RunShape {
  /** The page takes several files and merges the rest into the first. */
  readonly takesMany: boolean;
  /** A run hands back a second part as well, which is its own download. */
  readonly givesParts: boolean;
}

export function runShape(ops: readonly string[]): RunShape {
  return {
    takesMany: ops.includes('mergeDocuments'),
    givesParts: ops.includes('splitDocument'),
  };
}

/**
 * The subtitle number whose start a build of these values needs as
 * `RunInputs.cueStart`, or `null` when it needs none. Here rather than in the
 * page so the page never learns which key the split row calls it.
 */
export function cueNeeded(ops: readonly string[], values: ToolValues): number | null {
  if (!ops.includes('splitDocument')) return null;
  const cue = bucket(ops, values).get('splitDocument')?.['cue'];
  return typeof cue === 'number' ? cue : null;
}

/**
 * Stand-in inputs, for asking whether a row CAN be built before any file is
 * dropped: the tool page's mount-time check, and the guards over published
 * rows. The id is never sent anywhere; nothing resolves it.
 */
export function probeInputs(ops: readonly string[]): RunInputs {
  return runShape(ops).takesMany ? { others: ['probe' as DocumentId] } : {};
}

/**
 * One builder per op. `vals` is that op's OWN values, already de-prefixed.
 *
 * Every builder is total: it reads what the schema offers and supplies the
 * contract's default for everything else, because a tool page exposes a SUBSET
 * of an op's fields by design and an op missing a required field is a defect,
 * not a partial configuration.
 *
 * A builder returns a LIST when one control surface is several ops: merging
 * three files is two `mergeDocuments`, one per file added to the first.
 */
type Builder = (vals: ToolValues, all: ToolValues, inputs: RunInputs) => Op | readonly Op[];

/** The merge page's own default for "Treat lines as matching when they start within". */
const MERGE_THRESHOLD_MS = 500;

const BUILDERS: Readonly<Record<string, Builder>> = {
  normalize: (v) => ({
    op: 'normalize',
    respectLayers: bool(v, 'respectLayers', true),
    dropEmpty: str(v, 'dropEmpty', 'text-and-drawings') as 'text-and-drawings' | 'text-only',
  }),

  renumber: () => ({ op: 'renumber' }),

  stripFormatting: (v, all) => ({
    op: 'stripFormatting',
    where: ALL,
    scope: str(v, 'scope', 'both') as 'inline' | 'style' | 'both',
    keepItalic: bool(v, 'keepItalic', true),
    keepBold: bool(v, 'keepBold', false),
    keepUnderline: bool(v, 'keepUnderline', false),
    keepColor: bool(v, 'keepColor', false),
    keepFont: bool(v, 'keepFont', false),
    keepClasses: bool(v, 'keepClasses', false),
    keepWordTiming: bool(v, 'keepWordTiming', true),
    rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
  }),

  stripDelimited: (v, all) => ({
    op: 'stripDelimited',
    where: ALL,
    // The schema offers one checkbox per bracket family; the op takes the pairs
    // themselves. `square`/`round` default ON because they are the SDH
    // conventions; the rest are opt-in, per `patterns.ts`.
    //
    // `asterisk` and `hash` were offered on the page and read nowhere, so
    // ticking "Asterisks *…*" left "*sighs*" in the file. `delimitersOf` in
    // stripDelimited.ts takes an identical open and close, so they are plain
    // pairs like the rest.
    pairs: [
      ...(bool(v, 'square', true) ? [{ open: '[', close: ']' }] : []),
      ...(bool(v, 'round', true) ? [{ open: '(', close: ')' }] : []),
      ...(bool(v, 'curly', false) ? [{ open: '{', close: '}' }] : []),
      ...(bool(v, 'asterisk', false) ? [{ open: '*', close: '*' }] : []),
      ...(bool(v, 'hash', false) ? [{ open: '#', close: '#' }] : []),
      ...(bool(v, 'angle', false) ? [{ open: '<', close: '>' }] : []),
      ...(bool(v, 'fullWidth', false) ? [{ open: '（', close: '）' }] : []),
      ...(bool(v, 'lenticular', false) ? [{ open: '【', close: '】' }] : []),
    ],
    dropCueIfEmpty: bool(v, 'dropCueIfEmpty', true),
    rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
  }),

  removeSdh: (v, all) => {
    // A MASTER SWITCH GATES ITS CHILDREN HERE, because the op has no switch of
    // its own for them to sit under. "Subtitle-site credits and watermarks" is
    // `watermarksEnabled` and `RemoveSdhOp` has only the pattern list; this
    // builder read the list and never the switch, and all four patterns that
    // ship on stayed on underneath it. Turned off, the credit line
    // "Subtitles by ripper" was still deleted (test/tools/clean-switches-end-to-end.test.ts).
    //
    // `soundEffects` and `musicNotes` ARE fields on the op and the engine
    // already skips their patterns when either is false, so the output was
    // right; the lists are emptied as well so the op says what will run, and
    // the guard in ops-from-values.test.ts can hold every master switch to one
    // rule.
    const soundEffects = bool(v, 'soundEffects', true);
    const musicNotes = bool(v, 'musicNotes', true);
    const watermarksOn = bool(v, 'watermarksEnabled', true);
    return {
      op: 'removeSdh',
      where: ALL,
      speakerLabels: bool(v, 'speakerLabels', true),
      soundEffects,
      soundEffectPatterns: soundEffects
        ? enabledPatterns(SOUND_EFFECT_PATTERNS, v, 'soundEffectPatterns')
        : [],
      musicNotes,
      // The three checkboxes under "Music notes" had no field to reach until
      // contract 1.6.0 gave the op this list, so "[MUSIC] / [MUSIC PLAYING]
      // cues" ticked left [MUSIC] in the file and "Music notes around a line"
      // unticked still deleted every note.
      musicPatterns: musicNotes ? enabledPatterns(MUSIC_PATTERNS, v, 'musicPatterns') : [],
      watermarks: watermarksOn ? enabledPatterns(WATERMARK_PATTERNS, v, 'watermarks') : [],
      dropCueIfEmpty: bool(v, 'dropCueIfEmpty', true),
      rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
    };
  },

  unwrap: (v, all) => ({
    op: 'unwrap',
    where: ALL,
    joiner: str(v, 'joiner', ' '),
    cjkJoiner: str(v, 'cjkJoiner', ''),
    rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
  }),

  case: (v, all) => ({
    op: 'case',
    where: ALL,
    mode: str(v, 'mode', 'sentence') as 'upper' | 'lower' | 'sentence' | 'title',
    locale: null,
    continuesAcrossCues: bool(v, 'continuesAcrossCues', true),
    protectAcronyms: bool(v, 'protectAcronyms', true),
    // Sentence-boundary vocabulary as DATA, per the contract: '3.5 million' and
    // 'Mr. Smith' break sentence case without them, and CJK terminators are a
    // different set rather than an afterthought.
    sentenceTerminators: ['.', '!', '?', '。', '！', '？'],
    abbreviations: ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.', 'St.', 'vs.', 'etc.'],
    cjk: CJK_DEFAULTS,
    rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
  }),

  mergeSameText: (v, all) => ({
    op: 'mergeSameText',
    where: ALL,
    respectLayers: bool(v, 'respectLayers', true),
    // The box is in ms (the schema derives its 200 from this very constant);
    // the fallback is already ticks. Mixing the two in one `num()` call sent
    // 200 ticks, 1.7 ms, and nothing a user would call "the same line" merged.
    threshold:
      typeof v['threshold'] === 'number'
        ? msDelta(v['threshold'] as number)
        : MERGE_SAME_TEXT_THRESHOLD,
    compare: str(v, 'compare', 'trimmed') as 'exact' | 'trimmed' | 'stripped',
    rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
  }),

  replace: (v, all) => ({
    op: 'replace',
    where: ALL,
    find: str(v, 'find', ''),
    replace: str(v, 'replace', ''),
    literal: bool(v, 'literal', true),
    maxConfidence: null,
    rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
  }),

  reflow: (v, all) => {
    // `break` is a nested object the schema flattens into six sibling controls.
    const brk: LineBreakOptions = {
      ...LINE_BREAK_DEFAULTS,
      maxCells: num(v, 'maxCells', LINE_BREAK_DEFAULTS.maxCells),
      maxLines: num(v, 'maxLines', LINE_BREAK_DEFAULTS.maxLines),
      algorithm: str(v, 'algorithm', LINE_BREAK_DEFAULTS.algorithm) as 'greedy' | 'balanced',
      breakOnClause: bool(v, 'breakOnClause', LINE_BREAK_DEFAULTS.breakOnClause),
      clauseBreakers: CLAUSE_BREAKERS,
      // "Use Chinese and Japanese line-breaking rules" was read nowhere: this
      // was `CJK_DEFAULTS` whatever the box said. Its help text names the rules
      // it means — no line ending on an opening bracket or starting with a full
      // stop — which are the two kinsoku sets, so OFF empties those and nothing
      // else. It does NOT set `enabled: false`: in linebreak.ts that also
      // removes every break opportunity between two CJK characters, and an
      // unspaced Chinese line would then never break at all.
      cjk: bool(v, 'cjk', true)
        ? CJK_DEFAULTS
        : { ...CJK_DEFAULTS, noBreakBefore: '', noBreakAfter: '' },
    };
    return {
      op: 'reflow',
      where: ALL,
      break: brk,
      overflow: str(v, 'overflow', 'allow-extra-lines') as
        | 'allow-extra-lines'
        | 'overflow-last-line'
        | 'split-cue',
      preserveHardBreaks: bool(v, 'preserveHardBreaks', false),
      // Frozen `true` in the contract: reflowing must not move a word-timed run
      // boundary, because that is the per-word timing this product's
      // differentiator formats are built on.
      keepTimedRunBoundaries: true,
      rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
    };
  },

  fixTiming: (v) => {
    // These four ship EMPTY on the page — the founder has not set the
    // thresholds — and `null` is the op's own "this constraint is off". A
    // rendered default would be adoption in the only way a user experiences it.
    //
    // All four boxes are `unit: 'ms'` and the op takes ticks. Unconverted, a
    // "Maximum time on screen" of 7000 was 7000 ticks, 58 ms, and every cue in
    // the file was cut to 58 ms.
    const minGap = msDeltaOrNull(numOrNull(v, 'minGap'));
    const fixOverlap = bool(v, 'fixOverlap', true);
    // The strategy decides who gives way whenever fix-timing.ts separates cues,
    // which is `fixOverlap || minGap !== null`, and the page enables it on the
    // same two fields (`enabledBy`). With neither set the engine never reads
    // it, so the op carries the default rather than a value from a greyed-out
    // control: the same file either way, and the op says what will run.
    const separates = fixOverlap || minGap !== null;
    return {
      op: 'fixTiming',
      where: ALL,
      minDuration: msDeltaOrNull(numOrNull(v, 'minDuration')),
      maxDuration: msDeltaOrNull(numOrNull(v, 'maxDuration')),
      minGap,
      closeGapsUnder: msDeltaOrNull(numOrNull(v, 'closeGapsUnder')),
      fixOverlap,
      respectLayers: bool(v, 'respectLayers', true),
      overlapStrategy: (separates ? str(v, 'overlapStrategy', 'trim-earlier') : 'trim-earlier') as
        | 'trim-earlier'
        | 'push-later'
        | 'split-difference',
      grow: str(v, 'grow', 'end') as 'end' | 'both',
      neverCollide: bool(v, 'neverCollide', true),
    };
  },

  shift: (v) => {
    // THE DERIVATION, and the reason a generic prefix-strip cannot do this job.
    // The page asks for a delta, a "different amounts for different parts"
    // switch and a list of rows. `ShiftOp` takes ranges and nothing else, so
    // the simple case is expressed as one range covering the whole file.
    //
    // ⚠ THIS SHIPPED AS `{from: 0, to: 0}`, and `shift.ts` matches a cue when
    // `from <= start < to` — a half-open range that is EMPTY when the two are
    // equal. Every shift on /shift-subtitles moved nothing, and the page said
    // "No changes" over an untouched download. The per-range rows did the same
    // with To = 0, which the help text promises means "to the end of the file".
    // Both now build an open-ended range, and every number is converted from the
    // page's milliseconds to ticks here, once.
    const useRanges = bool(v, 'useRanges', false);
    const rows = Array.isArray(v['ranges']) ? (v['ranges'] as ToolValues[]) : [];
    const ranges: readonly ShiftRange[] = useRanges
      ? rows.map((r) => {
          const to = num(r, 'range.to', 0);
          return {
            from: msToTicks(num(r, 'range.from', 0)),
            to: to === 0 ? END_OF_FILE : msToTicks(to),
            delta: msDelta(num(r, 'range.delta', 0)),
          };
        })
      : [{ from: START_OF_FILE, to: END_OF_FILE, delta: msDelta(num(v, 'delta', 0)) }];
    return { op: 'shift', ranges, clampAtZero: bool(v, 'clampAtZero', true) };
  },

  framerate: (v) => ({
    op: 'framerate',
    // An unset box is `null` and stays `null`: no rate is assumed, and the
    // engine refuses the op until the user types one.
    from: labelled(v['from']) as Framerate,
    to: labelled(v['to']) as Framerate,
    mode: str(v, 'mode', 'rescale') as 'rescale' | 'reinterpret' | 'resnap',
    // The user typed both rates on the page. The contract's word for that is
    // 'user'; this sent 'stated', which `FramerateSource` does not contain,
    // behind an `as never` that kept the compiler from saying so.
    source: 'user' satisfies FramerateSource,
  }),

  twoPointSync: (v) => ({
    // Four flat duration fields, in milliseconds, become two anchors in ticks.
    // The fields arrive as `sync.*`; see `NAMESPACE_OWNER` for how they get here.
    op: 'twoPointSync',
    a: { at: msToTicks(num(v, 'aAt', 0)), to: msToTicks(num(v, 'aTo', 0)) },
    b: { at: msToTicks(num(v, 'bAt', 0)), to: msToTicks(num(v, 'bTo', 0)) },
    extrapolate: bool(v, 'extrapolate', true),
  }),

  pinyin: (v) => ({
    op: 'pinyin',
    where: ALL,
    // The schema's own default, for a caller that sends no mode: see the pinyin row in
    // src/transforms/structure/schema.ts for why it is not 'above'.
    mode: str(v, 'mode', 'extra-line') as 'above' | 'replace' | 'extra-line',
    tone: str(v, 'tone', 'marks') as 'marks' | 'numbers' | 'none',
    spacing: str(v, 'spacing', 'syllable') as 'syllable' | 'word',
  }),

  color: (v) => ({
    op: 'color',
    where: ALL,
    color: (v['color'] ?? { r: 255, g: 255, b: 255, a: 1 }) as Color,
    scope: str(v, 'scope', 'inline') as 'inline' | 'style',
  }),

  position: (v) => {
    // Three flat margin numbers behind a switch become one object or null —
    // `null` means "leave the margins alone", which is not the same as zero.
    const margins: Margins | null = bool(v, 'setMargins', false)
      ? {
          left: num(v, 'marginLeft', 0),
          right: num(v, 'marginRight', 0),
          vertical: num(v, 'marginVertical', 0),
        }
      : null;
    return {
      op: 'position',
      where: ALL,
      alignment: (v['alignment'] ?? null) as Alignment | null,
      margins,
      clearAbsolute: bool(v, 'clearAbsolute', true),
    };
  },

  mergeDocuments: (v, all, inputs) => {
    const others = inputs.others ?? [];
    if (others.length === 0) {
      throw new UnbuildableOp('mergeDocuments', NEEDS_RUNTIME_INPUT['mergeDocuments'] as string);
    }
    // "Put the second language on its own line" OFF is what unlocks the two
    // joiners (`childrenWhen: [false]`). While it is on they are greyed out,
    // so they carry the defaults rather than whatever the boxes hold: the same
    // file either way, and the op says what will run.
    const lineBreakBetween = bool(v, 'lineBreakBetween', true);
    const shared = {
      op: 'mergeDocuments' as const,
      mode: str(v, 'mode', 'simple') as MergeMode,
      // A `duration` box, so milliseconds; the op takes ticks.
      threshold: msDelta(num(v, 'threshold', MERGE_THRESHOLD_MS)),
      positionOther: (v['positionOther'] ?? null) as Alignment | null,
      colorOther: (v['colorOther'] ?? null) as Color | null,
      lineBreakBetween,
      joiner: lineBreakBetween ? ' ' : str(v, 'joiner', ' '),
      cjkJoiner: lineBreakBetween ? '' : str(v, 'cjkJoiner', ''),
      separateTrack: bool(v, 'separateTrack', true),
      rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
    };
    // ONE OP PER FILE ADDED, each folding the next file into what is merged so
    // far, so three files come out in the order the user gave them.
    //
    // Two fields only mean something on the FIRST of them:
    //
    //   glueOffset    "Length of the first video". A later op's base is the
    //                 first two files glued together, so the same number would
    //                 put file three on top of file two. Null there, which the
    //                 op reads as "after the last subtitle so far".
    //   positionBase  "Put the first file". A later op's base already holds
    //                 file two, and repositioning the base would move file two
    //                 onto the first file's row.
    return others.map((otherId, i) => ({
      ...shared,
      otherId,
      glueOffset: i === 0 ? msDeltaOrNull(numOrNull(v, 'glueOffset')) : null,
      positionBase: i === 0 ? ((v['positionBase'] ?? null) as Alignment | null) : null,
    }));
  },

  splitDocument: (v, all, inputs) => {
    // The cut point, from whichever box the user filled in. "Before subtitle
    // number" wins when it holds a number, because it is the more specific of
    // the two and its help says so. Its time comes from the file (`cueStart`),
    // so building it without one is refused rather than guessed.
    const cue = numOrNull(v, 'cue');
    let at: Ticks | null;
    if (cue !== null) {
      if (inputs.cueStart === undefined) {
        throw new UnbuildableOp(
          'splitDocument',
          'a cut before a subtitle number needs that subtitle’s start time, looked up in the file',
        );
      }
      // A number past the last subtitle cuts after everything: part two is
      // empty, which split.ts defines rather than refuses, and the page says so.
      at = inputs.cueStart ?? END_OF_FILE;
    } else {
      const ms = numOrNull(v, 'at');
      // An empty "Cut at" box is no cut point yet. It goes to the engine as
      // null, which `splitDocument` refuses as invalid, so the page offers no
      // download until there is one. A default of 0 used to stand here, and a
      // cut at 0 is a "part one" with nothing in it.
      at = ms === null ? null : msToTicks(ms);
    }
    // "Start the second file from zero" is a switch over "Start it at": off,
    // the offset is inert in the engine as well (split.ts), so it is sent as
    // zero rather than as a value from a greyed-out box.
    const retimeSecondPart = bool(v, 'retimeSecondPart', false);
    return {
      op: 'splitDocument',
      at: at as Ticks,
      retimeSecondPart,
      secondPartOffset: retimeSecondPart ? msDelta(num(v, 'secondPartOffset', 0)) : msDelta(0),
      splitStraddlingCue: bool(v, 'splitStraddlingCue', false),
      rawTagPolicy: str(all, 'rawTagPolicy', RAW_TAGS),
    };
  },
};

/**
 * Why an op that needs a file-derived input could not be built without it, so
 * the failure names the reason instead of surfacing as a malformed op.
 *
 * Both document-level ops USED to be listed here as unbuildable outright, and
 * /merge-subtitles and /split-subtitles refused at mount ("This tool isn't
 * working") while published, in the sitemap, with copy promising both. The
 * page had one dropzone and one download. It now parses every file it is given
 * and passes the extra ones in as `RunInputs.others`, and offers split's
 * spawned part two as its own download (`runShape`). What remains is the
 * honest failure for a caller that supplies neither.
 */
const NEEDS_RUNTIME_INPUT: Readonly<Record<string, string>> = {
  mergeDocuments:
    'needs the second document’s id, which comes from a parsed file and no option control can supply',
};

export class UnbuildableOp extends Error {
  readonly opName: string;
  constructor(opName: string, reason: string) {
    super(`cannot build op '${opName}': ${reason}`);
    this.name = 'UnbuildableOp';
    this.opName = opName;
  }
}

/**
 * Key prefixes a published schema uses that are NOT the name of the op they
 * configure.
 *
 * /sync-subtitles publishes `sync.aAt`, `sync.aTo`, `sync.bAt`, `sync.bTo` and
 * `sync.extrapolate` for the op `twoPointSync`. A dotted key is routed by its
 * prefix, no op is called `sync`, so all five were dropped: the op was built
 * from its defaults, both anchors at 0, and the engine refused it as a
 * divide-by-zero whatever the user typed. The page could never sync anything.
 *
 * Mapped here rather than renamed in the schema, because the keys are a
 * published surface (`server/app/tools.json`, and the page bundle that reads
 * it). `test/tools/ops-from-values.test.ts` checks that every key of every
 * published row reaches an op, so a new mismatch fails there.
 */
const NAMESPACE_OWNER: ReadonlyMap<string, string> = new Map([['sync', 'twoPointSync']]);

/**
 * The op in `ops` that a value-bag key configures, or `null` when it reaches
 * none and would be dropped. Bare keys belong to `ops[0]`.
 */
export function ownerOf(ops: readonly string[], key: string): string | null {
  const dot = key.indexOf('.');
  if (dot <= 0) return ops[0] ?? null;
  const prefix = key.slice(0, dot);
  const owner = NAMESPACE_OWNER.get(prefix) ?? prefix;
  return ops.includes(owner) ? owner : null;
}

/**
 * What a value-bag key configures: the op `ownerOf` names, or else the report
 * the page reads after the run, or `null` when the key reaches nothing.
 *
 * /subtitle-reading-speed-checker's `qc.*` keys had no op to reach and nothing
 * else read them, so the guard over published keys carried them as a standing
 * exception. They configure the QC report (src/tools/qc-from-values.ts), and
 * this is the one place that says so, for the guard and for anyone asking.
 */
export function consumerOf(ops: readonly string[], key: string): string | null {
  const op = ownerOf(ops, key);
  if (op !== null) return op;
  return hasQcControls([key]) ? QC_NAMESPACE : null;
}

/** Split a flat value bag into per-op buckets, by `ownerOf`. */
function bucket(ops: readonly string[], values: ToolValues): Map<string, Record<string, unknown>> {
  const out = new Map<string, Record<string, unknown>>();
  for (const op of ops) out.set(op, {});
  for (const [key, value] of Object.entries(values)) {
    const owner = ownerOf(ops, key);
    const dot = key.indexOf('.');
    const field = dot > 0 ? key.slice(dot + 1) : key;
    const target = owner !== null ? out.get(owner) : undefined;
    if (target) target[field] = value;
  }
  return out;
}

/**
 * The ops a tool page should send, built from what the user actually set.
 *
 * Returns the ops AND the names it dropped, because "your pipeline is shorter
 * than the row declared" is a fact the caller may want to show and must never
 * discover by comparing lengths.
 */
export function opsFromValues(
  ops: readonly string[],
  values: ToolValues,
  inputs: RunInputs = {},
): { readonly ops: readonly Op[]; readonly disabled: readonly string[] } {
  const buckets = bucket(ops, values);
  const built: Op[] = [];
  const disabled: string[] = [];

  for (const name of ops) {
    const vals = buckets.get(name) ?? {};
    // `<op>.enabled === false` means the user turned this step off. It is a
    // pipeline filter and never reaches the op — no `Op` has such a field.
    if (vals['enabled'] === false) {
      disabled.push(name);
      continue;
    }
    const build = BUILDERS[name];
    if (!build) throw new UnbuildableOp(name, 'no builder is registered for it');
    const made = build(vals, values, inputs);
    if (Array.isArray(made)) built.push(...made);
    else built.push(made as Op);
  }
  return { ops: built, disabled };
}

/** The op names this layer can build — for a guard that keeps rows and builders in step. */
export const BUILDABLE_OPS: readonly string[] = Object.keys(BUILDERS);
